import React, { useCallback, useEffect, useState } from 'react';

import { styled } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import { token, useThemeObserver } from '@atlaskit/tokens';

import { type CarouselProps } from './types';

export const Carousel = ({
	items,
	selectedItem,
	onFocusItem,
	slideInterval,
	testId,
}: CarouselProps) => {
	const [currentItem, setCurrentItem] = useState(selectedItem ? selectedItem : 0);
	const [isSliding, setIsSliding] = useState(Boolean(slideInterval) && selectedItem === undefined);
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	const onChangeItem = useCallback(
		(index: number, shouldSlide: boolean, interactionType: 'automatic' | 'carouselToggle') => {
			setCurrentItem(index);
			onFocusItem && onFocusItem(interactionType, index);
			setIsSliding(shouldSlide);
		},
		[onFocusItem],
	);

	const onSlide = useCallback(() => {
		const nextIndex = (currentItem + 1) % items.length;
		onChangeItem(nextIndex, true, 'automatic');
	}, [currentItem, items.length, onChangeItem]);

	const onNext = useCallback(() => {
		const nextIndex = (currentItem + 1) % items.length;
		onChangeItem(nextIndex, false, 'carouselToggle');
	}, [currentItem, items.length, onChangeItem]);

	const onPrev = useCallback(() => {
		const prevIndex = (currentItem - 1 + items.length) % items.length;
		onChangeItem(prevIndex, false, 'carouselToggle');
	}, [currentItem, items.length, onChangeItem]);

	useEffect(() => {
		const interval = slideInterval && isSliding && setTimeout(onSlide, slideInterval * 1000);
		return () => {
			interval && clearTimeout(interval);
		};
	}, [currentItem, isSliding, slideInterval, onSlide]);

	useEffect(() => {
		if (selectedItem !== undefined && selectedItem !== currentItem) {
			setIsSliding(false);
			setCurrentItem(selectedItem);
		}
	}, [currentItem, selectedItem]);

	const { component, caption } = items[currentItem];

	return (
		<CarouselContainer data-testid={`${testId}-carousel`}>
			<ItemContainer>{isDarkMode ? component.dark : component.light}</ItemContainer>
			<ControlsContainer>
				<SlideCaption>{caption}</SlideCaption>
				<CarouselControls>
					<IconButton
						testId={`${testId}-carousel-previous-button`}
						appearance="subtle"
						icon={() => (
							<ChevronLeftLargeIcon
								label="To previous"
								primaryColor={token('color.icon.subtle', '#626F86')}
							/>
						)}
						label="To previous"
						onClick={onPrev}
					/>
					{items.map((_, index) => (
						<IndicatorDot key={index} selected={currentItem === index} />
					))}
					<IconButton
						testId={`${testId}-carousel-next-button`}
						appearance="subtle"
						icon={() => (
							<ChevronRightLargeIcon
								label="To next"
								primaryColor={token('color.icon.subtle', '#626F86')}
							/>
						)}
						label="To next"
						onClick={onNext}
					/>
				</CarouselControls>
			</ControlsContainer>
		</CarouselContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CarouselContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginBottom: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemContainer = styled.div({
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ControlsContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SlideCaption = styled.span({
	fontSize: '20px',
	color: token('color.text.subtle', '#44546F'),
	textAlign: 'center',
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CarouselControls = styled.div({
	height: '19px',
	display: 'flex',
	padding: token('space.0', '0'),
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.100', '8px'),
	alignSelf: 'stretch',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IndicatorDot = styled.span<{ selected?: boolean }>({
	height: '8px',
	width: '8px',
	borderRadius: '100px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) =>
		props.selected
			? token('color.icon.selected', '#0C66E4')
			: token('color.background.accent.blue.subtler', '#CCE0FF'),
});
